import styled from 'styled-components';
import { Text } from '@movember/mo-gel';
import { ReactComponent as HomeIcon } from '../assets/icon_home.svg';

export const StyledHeaderContainer = styled.div`
  display: flex;
  position: relative;
  vertical-align: middle;
  justify-content: space-between;
  padding: 20px 20px;
  margin: 0;
  background-color: ${(props) => props.theme.colors.white};

  @media screen and (min-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 20px 40px;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoints.lg}) {
    padding: 20px 93px;
  }
`;

export const StyledLogoLink = styled.a`
  line-height: 0;

  svg {
    height: 37px;
    width: 105px;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoints.md}) {
    svg {
      max-height: 67px;
      max-width: 113px;
      height: 100%;
      width: 100%;
    }
  }
`;

export const StyledMenuList = styled.ul`
  display: flex;
  align-items: center;
  margin: 0;
`;

export const PopoverMenuContainer = styled.ul`
  visibility: hidden;
  position: absolute;
  left: -100%;
  transform: translate(0, 10px);
  background-color: ${(props) => props.theme.colors.brandThemeDark900};
  box-shadow: 0 4px 10px 0 rgba(11, 41, 68, 0.3);
  border-radius: 5px;
  height: auto;
  box-sizing: border-box;
  width: 350px;
  overflow: unset;
  min-width: 250px;
  padding-left: 0;

  :before {
    position: absolute;
    z-index: -1;
    content: '';
    right: 50%;
    top: -8px;
    border-style: solid;
    border-width: 0 10px 10px 10px;
    border-color: transparent transparent ${(props) => props.theme.colors.brandThemeDark900} transparent;
    transition-duration: 0.3s;
    transition-property: transform;
  }
`;

export const StyledHomeIcon = styled(HomeIcon)`
  margin-left: 10px;
  margin-bottom: 0.17em;
`;

export const StyledMenuListItem = styled.li`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors.primaryFont};
  font-weight: 700;
  position: relative;
  padding: 5px 0;

  @media screen and (min-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 15px 25px;

    :hover {
      color: ${(props) => props.theme.colors.actionPrimary300};

      ${StyledHomeIcon} {
        fill: ${(props) => props.theme.colors.actionPrimary300};
      }
    }
  }

  ${(props) =>
    props.hasPopover &&
    `
    :hover:after {
      position: absolute;
      content: '';
      height: 95px;
      width: 150px;
      z-index: 2;
      left: -15px;
      cursor: pointer;
    }
  `}

  :hover ${PopoverMenuContainer} {
    z-index: 10;
    opacity: 1;
    top: 95px;
    visibility: visible;
    transform: translate(0, -25px);
    transition: all 0.5s cubic-bezier(0.75, -0.02, 0.2, 0.97);
    border: 1px solid ${(props) => props.theme.colors.brandThemeDark700}
  }
`;

export const StyledMenuItemLink = styled.a`
  text-decoration: none;
  background-image: ${(props) =>
    `linear-gradient(to right, ${props.theme.colors.actionPrimary300}, ${props.theme.colors.actionPrimary300})`};
  background-size: 1px 0.112em;
  background-position: left 0 bottom 0;
  background-repeat: repeat-x;
  color: inherit;
  display: flex;
  align-items: flex-end;

  :visited {
    color: inherit;
  }
`;

export const PopoverMenuItem = styled.li`
  display: inline-block;
  position: relative;
  margin: 0;
  box-sizing: border-box;
  width: 100%;
  white-space: nowrap;
  border-radius: 5px;
  text-align: left;
  font-weight: 400;

  :hover {
    background-color: ${(props) => props.theme.colors.brandThemeDark700};
  }
`;

export const PopoverMenuLink = styled.a`
  display: inline-block;
  color: ${(props) => props.theme.colors.white};
  text-decoration: none;
  background-image: none;
  box-sizing: border-box;
  width: inherit;
  padding: 10px 70px 10px 24px;

  :hover {
    :after {
      cursor: pointer;
      position: absolute;
      right: 10px;
      top: calc(50% - 15px);
      content: 'chevron_right';
      font-family: 'Material Icons';
      font-size: 1.4em;
      line-height: 30px;
      text-align: center;
      background-color: ${(props) => props.theme.colors.actionPrimary300};
      border-radius: 50%;
      width: 30px;
      height: 30px;
      color: ${(props) => props.theme.colors.white};
    }
  }
`;

export const StyledLinkTextWrapper = styled(Text)`
  white-space: pre-wrap;
`;

export const StyledMenuToggle = styled.div`
  display: block;
  padding-left: 12px;
  padding-top: 4px;
  margin-bottom: 0.17em;

  @media screen and (min-width: ${(props) => props.theme.breakpoints.sm}) {
    padding-left: 18px;
    padding-top: 7px;
  }

  -webkit-user-select: none;
  user-select: none;

  input {
    display: block;
    width: 80px;
    height: 40px;
    position: absolute;
    cursor: pointer;
    opacity: 0;
    z-index: 2;
    left: 0;
    top: 0;

    -webkit-touch-callout: none;

    :checked {
      ~ span {
        opacity: 1;
        transform: rotate(45deg) translate(0, 4px);
        background: ${(props) => props.theme.colors.black};

        :nth-last-child(3) {
          opacity: 0;
          transform: rotate(0deg) scale(0.2, 0.2);
        }

        :nth-last-child(2) {
          transform: rotate(-45deg) translate(-2.5px, 0);
        }
      }
    }
  }

  span {
    display: block;
    width: 20px;
    height: 2px;
    margin-bottom: 4px;
    position: relative;
    background: ${(props) => props.theme.colors.black};
    border-radius: 3px;
    z-index: 1;
    transform-origin: 4px 0px;

    transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1), opacity 0.55s ease;

    :first-child {
      transform-origin: 0% 0%;
    }

    :nth-last-child(2) {
      transform-origin: 0% 100%;
    }
    @media screen and (min-width: ${(props) => props.theme.breakpoints.sm}) {
      margin-bottom: 3.8px;
    }
  }
`;

export const StyledMobileContainer = styled.div`
  padding: 30px 30px;

  @media screen and (min-width: ${(props) => props.theme.breakpoints.sm}) {
    padding: 50px 80px;
  }
`;

export const StyledSubLinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 0;
`;
